import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  getOnboardingMessages,
  storeOnboardingMessage,
} from "../../../../../services/vmApi";

export default function OnboardingChat({ companyCode, currentUserId, enabled }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef(null);

  const { data: messages, isLoading } = useQuery(
    ["onboarding-messages", companyCode],
    () =>
      getOnboardingMessages(companyCode).then(
        res => res.data?.data ?? res.data ?? [],
      ),
    {
      enabled: !!companyCode && enabled,
      refetchOnWindowFocus: false,
    },
  );

  const { mutate: sendMessage, isLoading: isSending } = useMutation(
    newMessage => storeOnboardingMessage(companyCode, newMessage),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["onboarding-messages", companyCode]);
        setMessage("");
      },
      onError: () => {
        toast.error("Erro ao enviar mensagem.");
      },
    },
  );

  const messageList = Array.isArray(messages) ? messages : [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messageList.length]);

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed || isSending) return;
    sendMessage(trimmed);
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Conversa
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          bgcolor: "#f5f5f5",
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : messageList.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", mt: 2 }}
          >
            Nenhuma mensagem ainda. Inicie a conversa.
          </Typography>
        ) : (
          messageList.map(msg => {
            const isMine = msg.sender?.id === currentUserId;
            return (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent: isMine ? "flex-end" : "flex-start",
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: "80%",
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: isMine ? "primary.main" : "#fff",
                    color: isMine ? "primary.contrastText" : "text.primary",
                  }}
                >
                  {!isMine && msg.sender?.name && (
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ display: "block", mb: 0.25, opacity: 0.8 }}
                    >
                      {msg.sender.name}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {msg.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.25,
                      textAlign: "right",
                      opacity: 0.7,
                    }}
                  >
                    {msg.createdAt
                      ? new Date(msg.createdAt).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : ""}
                  </Typography>
                </Paper>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Box
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Digite uma mensagem..."
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSending}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!message.trim() || isSending}
        >
          {isSending ? <CircularProgress size={20} /> : <Send />}
        </IconButton>
      </Box>
    </Box>
  );
}
